import React, { useState, useMemo } from 'react';
import { Goal, Category } from '../../types';
import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../ui/command';
import { ChevronsUpDown, Check, ChevronRight, ChevronDown } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Badge } from '../ui/badge';

// --- GoalSelectItem Props and Component ---
interface GoalSelectItemProps {
    goal: Goal;
    allGoals: Goal[]; // This should contain all potential goals for structure
    level: number;
    onSelect: (goalId: number | null) => void;
    expanded: boolean;
    onToggleExpand: (goalId: number) => void;
    currentSelectedValue: number | null;
    maxDepth: number;
    expandedGoals: Record<number, boolean>;
    filterActiveOnly: boolean; // Pass filter flag down
    categories: Category[]; // Pass categories down for badge lookup
}

const GoalSelectItem: React.FC<GoalSelectItemProps> = ({
    goal,
    allGoals,
    level,
    onSelect,
    expanded,
    onToggleExpand,
    currentSelectedValue,
    maxDepth,
    expandedGoals,
    filterActiveOnly, // Use the flag
    categories,
}) => {
    // Find children from the *complete* list of goals
    const subGoals = useMemo(() => allGoals.filter(g => g.parent_goal === goal.id), [allGoals, goal.id]);
    const hasChildren = subGoals.length > 0;
    const isCurrentSelection = goal.id === currentSelectedValue;
    const canExpand = level < maxDepth -1;
    const isActive = !goal.completed_at;

    const handleSelect = () => {
        // Only allow selection if not filtering active OR if it is active
        if (!filterActiveOnly || isActive) {
            onSelect(goal.id);
        }
    };

    // Decide if this item itself should be rendered/selectable
    // It should be rendered if we are not filtering or if it's active.
    const shouldRenderItem = !filterActiveOnly || isActive;

    const categoryDetails = goal.category_details ?? categories.find(c => c.id === goal.category);

    return (
        <>
            {/* Conditionally render the item based on filter */} 
            {shouldRenderItem && (
                 <CommandItem
                    key={goal.id}
                    value={`${goal.title} ${goal.id}`}
                    // Make non-selectable items visually distinct and non-interactive if needed, but here we just don't render
                    onSelect={handleSelect}
                    style={{ paddingLeft: `${level * 1.5 + 0.5}rem` }}
                    className="flex items-center justify-between w-full cursor-pointer"
                >
                    <div className="flex items-center flex-grow overflow-hidden mr-2">
                        {/* Expand/Collapse Button - logic remains the same */} 
                        <Button
                            variant="ghost"
                            size="icon"
                            className={cn("h-5 w-5 p-0 mr-1 flex-shrink-0", (!hasChildren || !canExpand) && "invisible")}
                            onClick={(e: React.MouseEvent<HTMLButtonElement>) => { e.stopPropagation(); onToggleExpand(goal.id); }}
                            aria-label={expanded ? 'Collapse' : 'Expand'}
                        >
                            {hasChildren && canExpand && (expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />)}
                        </Button>

                        {/* Goal Title & Category */} 
                        <span className="truncate flex-grow mr-2">{goal.title}</span>
                        {categoryDetails?.name && <Badge variant="outline" className="ml-auto flex-shrink-0">{categoryDetails.name}</Badge>}
                    </div>
                    <Check
                        className={cn(
                            "h-4 w-4",
                            isCurrentSelection ? "opacity-100" : "opacity-0"
                        )}
                    />
                </CommandItem>
            )}

            {/* Recursion: Render children if expanded, regardless of parent's active status, but pass filter flag down */} 
            {hasChildren && expanded && canExpand && (
                subGoals.map(subGoal => (
                    <GoalSelectItem
                        key={subGoal.id}
                        goal={subGoal}
                        allGoals={allGoals} // Pass the full list down
                        level={level + 1}
                        onSelect={onSelect}
                        expanded={!!expandedGoals[subGoal.id]}
                        onToggleExpand={onToggleExpand}
                        currentSelectedValue={currentSelectedValue}
                        maxDepth={maxDepth}
                        expandedGoals={expandedGoals}
                        filterActiveOnly={filterActiveOnly} // Pass down
                        categories={categories} // Pass down
                    />
                ))
            )}
        </>
    );
};


// --- HierarchicalGoalSelect Props and Component --- 
interface HierarchicalGoalSelectProps {
    goals: Goal[]; // ALL goals including subgoals
    categories: Category[];
    value: number | null;
    onChange: (value: number | null) => void;
    placeholder?: string;
    disabled?: boolean;
    allowClear?: boolean;
    className?: string;
    filterActiveOnly?: boolean; // Filter out completed goals from *selection* 
    maxDepth?: number;
}

export const HierarchicalGoalSelect: React.FC<HierarchicalGoalSelectProps> = ({
    goals,
    categories,
    value,
    onChange,
    placeholder = "Select a goal...",
    disabled = false,
    allowClear = true,
    className,
    filterActiveOnly = false, // Default remains false
    maxDepth = 5
}) => {
    const [open, setOpen] = useState(false);
    const [expandedGoals, setExpandedGoals] = useState<Record<number, boolean>>({});

    // Use the original, unfiltered list for structure and finding the selected goal display
    const selectedGoal = useMemo(() => goals.find(goal => goal.id === value), [goals, value]);

    // Prepare the full list with details needed for rendering items and recursion
    const allGoalsWithDetails = useMemo(() => {
        return goals.map(g => ({
            ...g,
            category_details: g.category_details ?? categories.find(c => c.id === g.category)
        }));
    }, [goals, categories]);

    // Top level is determined from the full list
    const topLevelGoals = useMemo(() => allGoalsWithDetails.filter(g => !g.parent_goal), [allGoalsWithDetails]);

    const handleSelect = (goalId: number | null) => {
        onChange(goalId);
        setOpen(false);
    };

    const toggleExpand = (goalId: number) => {
        setExpandedGoals(prev => ({ ...prev, [goalId]: !prev[goalId] }));
    };

    const handleOpenChange = (newOpenState: boolean) => {
        setOpen(newOpenState);
        // Reset expansion state only if needed (e.g., clear on close or keep state)
        // Keeping previous simple toggle for now, user rejected auto-expand
    }

    return (
        <Popover open={open} onOpenChange={handleOpenChange}>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    className={cn("w-full justify-between", className)}
                    disabled={disabled}
                >
                    <span className="truncate">
                        {selectedGoal ? selectedGoal.title : placeholder}
                    </span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[--radix-popover-trigger-width] max-h-[--radix-popover-content-available-height] p-0">
                <Command>
                    <CommandInput placeholder="Search goals..." />
                    <CommandList>
                        <CommandEmpty>No goal found.</CommandEmpty>
                        <CommandGroup>
                            {allowClear && (
                                <CommandItem
                                    key="clear-selection"
                                    value="clear-selection"
                                    onSelect={() => handleSelect(null)}
                                >
                                    <span className="text-muted-foreground italic ml-1">
                                        (No Parent / Clear Selection)
                                    </span>
                                    <Check
                                        className={cn(
                                            "ml-auto h-4 w-4",
                                            value === null ? "opacity-100" : "opacity-0"
                                        )}
                                    />
                                </CommandItem>
                            )}
                            {/* Render top-level goals using the recursive component */} 
                            {topLevelGoals.map(goal => (
                                <GoalSelectItem
                                    key={goal.id}
                                    goal={goal}
                                    allGoals={allGoalsWithDetails} // Pass the full list with details
                                    level={0}
                                    onSelect={handleSelect}
                                    expanded={!!expandedGoals[goal.id]}
                                    onToggleExpand={toggleExpand}
                                    currentSelectedValue={value}
                                    maxDepth={maxDepth}
                                    expandedGoals={expandedGoals}
                                    filterActiveOnly={filterActiveOnly} // Pass the filter flag
                                    categories={categories} // Pass categories
                                />
                            ))}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
};

// export default HierarchicalGoalSelect; // Export if needed elsewhere directly 